import { performLogout } from '@/src/context/AuthContext'
import axios, { type InternalAxiosRequestConfig } from 'axios'
import { forceRefreshAccessToken, getOrRefreshAccessToken } from './session'

const scheme = 'Bearer'
const httpClient = axios.create({ baseURL: 'httpClient' })
const gygInternalApiClient = axios.create({ baseURL: 'gygInternalApiClient' })
const gygExternalApiClient = axios.create({ baseURL: 'gygExternalApiClient' })

const authInterceptor = async (config: InternalAxiosRequestConfig) => {
  const token = await getOrRefreshAccessToken()
  if (token) {
    config.headers.setAuthorization(`${scheme} ${token}`)
  }

  return config
}

const baseURLInterceptor = (config: InternalAxiosRequestConfig) => {
  switch (config.baseURL) {
    case 'httpClient':
      config.baseURL = process.env.NEXT_PUBLIC_R365_API_AU
      break
    case 'gygInternalApiClient':
      config.baseURL = process.env.NEXT_PUBLIC_BHYVE_INTERNAL_API_AU
      break
    case 'gygExternalApiClient':
      config.baseURL = process.env.NEXT_PUBLIC_BHYVE_EXTERNAL_API_AU
      break
  }

  return config
}

httpClient.interceptors.request.use(authInterceptor)
httpClient.interceptors.request.use(baseURLInterceptor)
gygInternalApiClient.interceptors.request.use(baseURLInterceptor)
gygExternalApiClient.interceptors.request.use(baseURLInterceptor)

let isLogoutInProgress = false
let unauthorizedRecovery: Promise<string> | null = null

const shouldLogoutOn401 = (url?: string): boolean => {
  if (!url) return true

  return !/\/(login|register)(\/|$|\?)/i.test(url)
}

const recoverAccessTokenAfter401 = async (): Promise<string> => {
  if (!unauthorizedRecovery) {
    unauthorizedRecovery = forceRefreshAccessToken().finally(() => {
      unauthorizedRecovery = null
    })
  }

  return unauthorizedRecovery
}

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean }

httpClient.interceptors.response.use(
  response => {
    return response
  },
  async error => {
    const status = error?.response?.status
    const requestUrl = String(error?.config?.url ?? '')
    const originalRequest = error?.config as RetriableRequestConfig | undefined

    if (status === 401 && shouldLogoutOn401(requestUrl) && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true
      const refreshedToken = await recoverAccessTokenAfter401()
      if (refreshedToken) {
        originalRequest.headers.setAuthorization(`${scheme} ${refreshedToken}`)

        return httpClient(originalRequest)
      }

      if (!isLogoutInProgress) {
        isLogoutInProgress = true
        try {
          // Skip Okta revoke on passive expiry so re-login is not rate-limited.
          await performLogout({ revokeOktaTokens: false })
        } finally {
          isLogoutInProgress = false
        }
      }
    }

    return Promise.reject(error)
  }
)

export { httpClient, gygInternalApiClient, gygExternalApiClient }
